import {
  IExperienceTemplateResource,
  ISubTaskConfirmTemplate,
  StorageContext
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import React, { SetStateAction } from 'react';
import {
  FormRow,
  FormRowGroup
} from '../../../../../../../../components/Form/FormRow';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import { TextFieldWithReplacementTokens } from '../../../../../../../../components/TextFieldWithReplacementTokens';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_CONFIRM
} from '../../../validation';
import { OnCompleteHooks, OnCompleteHooksReadOnly } from './onCompleteHooks';

export const BlockSettingsSubTaskConfirm = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl,
  customFields,
  tokens,
  resources,
  onChangeResources,
  goToSubPage,
  showOnCompleteHookSettings
}: {
  value: ISubTaskConfirmTemplate;
  onChange: (nextValue: ISubTaskConfirmTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  tokens: TokenDefinition[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const validateLabel = useFieldValidator(
    setValidation,
    VALIDATORS_CONFIRM,
    'label'
  );

  const validateHooks = useFieldValidator(
    setValidation,
    VALIDATORS_CONFIRM,
    'hooks'
  );

  const invalidLabelReason = validation?.fields.label || null;
  return (
    <>
      <FormRowGroup>
        <FormRow>
          <TextFieldWithReplacementTokens
            autoFocus
            label={t('Button Label')}
            value={value.data.label}
            fullWidth
            variant="outlined"
            error={!!invalidLabelReason}
            helperText={invalidLabelReason}
            onBlur={() => validateLabel(value, expTpl)}
            onChange={(label) => {
              const nextValue = {
                ...value,
                data: {
                  ...value.data,
                  label
                }
              };
              invalidLabelReason && validateLabel(nextValue, expTpl);
              onChange(nextValue);
            }}
            tokens={tokens}
          />
        </FormRow>
      </FormRowGroup>
      <OnCompleteHooks
        value={value.hooks.onComplete}
        tokens={tokens}
        onChange={(nextHooks) => {
          const nextValues = {
            ...value,
            hooks: {
              ...value.hooks,
              onComplete: nextHooks
            }
          };
          validateHooks(nextValues, expTpl);
          onChange(nextValues);
        }}
        resources={resources}
        onChangeResources={onChangeResources}
        expTpl={expTpl}
        customFields={customFields}
        goToSubPage={goToSubPage}
        validation={validation}
        showOnCompleteHookSettings={showOnCompleteHookSettings}
        storageContext={StorageContext.SUBTASK}
      />
    </>
  );
};

export const BlockSettingsSubTaskConfirmReadOnly = ({
  value,
  showOnCompleteHookSettings,
  ...props
}: {
  value: ISubTaskConfirmTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  return (
    <>
      {showOnCompleteHookSettings && (
        <OnCompleteHooksReadOnly
          value={value.hooks.onComplete}
          {...props}
          storageContext={StorageContext.SUBTASK}
        />
      )}
    </>
  );
};
